// src/pages/supplyList/SupplyCardView.jsx
import React from 'react';
import {
  Box, Card, CardContent, Typography, Chip
} from '@mui/material';

/**
 * 🚀 供給履歴カード表示コンポーネント
 * モバイル対応とユーザビリティを重視したカード表示
 */
const SupplyCardView = ({ 
  supplies, 
  onCardClick, 
  loading = false 
}) => {

  // データが空の場合の表示
  if (!Array.isArray(supplies) || supplies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {loading ? 'データを読み込み中...' : '供給履歴がありません'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'grid',
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: 'repeat(2, 1fr)',
        md: 'repeat(2, 1fr)'
      },
      gap: 2,
      mb: 2
    }}>
      {supplies.map((supply) => (
        <SupplyCard 
          key={supply.id}
          supply={supply}
          onClick={() => onCardClick(supply)}
        />
      ))}
    </Box>
  );
};

/**
 * 🔹 個別の供給履歴カードコンポーネント
 */
const SupplyCard = ({ supply, onClick }) => {
  
  // 日付フォーマット（安全な変換）
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '無効な日付';
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '無効な日付';
    }
  };

  // 供給量フォーマット
  const formatAmount = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return '0';
    return amount.toLocaleString();
  };

  // 🔧 修正：特例判定の安全な取得
  const isExceptional = supply.exceptional || supply.isExceptional || false;

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { 
          boxShadow: 6,
          transform: 'translateY(-2px)'
        },
        '&:active': {
          transform: 'translateY(0px)',
          boxShadow: 2
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2 }}>
        
        {/* ヘッダー行：日付と種別 */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 1.5 
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            {formatDate(supply.supplyDate)}
          </Typography>
          
          {/* 🔧 修正：APIレスポンスのプロパティ名に対応 */}
          <Chip
            label={isExceptional ? '特例' : '通常'}
            color={isExceptional ? 'warning' : 'success'}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }}
          />
        </Box>
        
        {/* 顧客名 */}
        <Typography 
          variant="h6"
          sx={{ 
            fontSize: '1.1rem',
            fontWeight: 500,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary'
          }}
          title={supply.clientName} // ツールチップ表示
        >
          {supply.clientName || '不明な顧客'}
        </Typography>
        
        {/* 製品と供給量 */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          mt: 2 
        }}>
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              製品
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500,
                color: 'text.primary'
              }}
            >
              {supply.productName || "アドブルー"}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              供給量
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: 'primary.main',
                whiteSpace: 'nowrap'
              }}
            >
              {formatAmount(supply.amount)} L
            </Typography>
          </Box>
        </Box>
        
        {/* 追加情報（あれば） */}
        {supply.note && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1,
              color: 'text.secondary',
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {supply.note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplyCardView;